import type {
  AttendanceOverview,
  PayrollOverview,
  EmployeesOverview,
  ProjectsOverview,
  UnconvertedAmount,
} from './types';

// KPIMetric type - inline to avoid circular dependency with components
// This matches the prop shape expected by KPICard.tsx
export type KPIMetric = {
  id: string;
  title: string;
  value: string;
  /** Optional: only present when a real prior-period comparison is available */
  change?: string;
  trend?: 'up' | 'down';
  /** True when `change` is informational (e.g. net hires−exits) and not a vs-previous delta */
  isNeutralChange?: boolean;
  icon: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'red';
  category: 'attendance' | 'payroll' | 'employee' | 'project';
  description: string;
  /**
   * CO-610 — amounts that belong to this metric but could not be folded into `value` (no FX rate to
   * the company currency). Rendered as "+ 12,000 AED" lines under the value, never summed into it.
   */
  supplement?: string[];
  /** CO-610 — why `supplement` exists, with the place to fix it. */
  notice?: { text: string; href: string; linkLabel: string };
};

// HolidayCard prop shape
export type HolidayCardData = {
  id: string;
  name: string;
  date: string;
  daysUntil: number;
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format a decimal value as a percentage string.
 * Returns "0%" if value is null/undefined.
 */
export function formatPercentage(value: number | null | undefined): string {
  if (value == null) return '0%';
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Calculate percentage change between current and previous values.
 * Returns null when no real comparison is possible.
 */
export function formatChange(
  current: number,
  previous: number | undefined
): { change: string; trend: 'up' | 'down' } | null {
  if (previous == null || previous === 0) return null;
  const delta = ((current - previous) / previous) * 100;
  const isUp = delta >= 0;
  const sign = isUp ? '+' : '';
  return {
    change: `${sign}${delta.toFixed(1)}%`,
    trend: isUp ? 'up' : 'down',
  };
}

/**
 * Format a currency value with the currency label.
 *
 * CO-610: the label is a currency CODE that follows the number ("349,840 SAR"), the same shape as the
 * payroll dashboard tiles, so the two surfaces read alike. The pre-CO-610 form was `${currency}${n}`
 * ("AED15,500"), which rendered a bare number whenever the label was empty (prod co 30).
 */
function formatCurrency(value: number | null | undefined, currency: string): string {
  const formatted = (value ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return currency ? `${formatted} ${currency}` : formatted;
}

/** "+ 12,000 AED" per currency the server could not convert. */
function formatUnconverted(parts: UnconvertedAmount[] | undefined): string[] {
  return (parts ?? [])
    .filter((p) => p.amount !== 0)
    .map((p) => `+ ${formatCurrency(p.amount, p.currencyCode)}`);
}

/** The FX settings page that owns company exchange rates. */
export const FX_SETTINGS_PATH = '/settings/currency-and-banks';

/**
 * CO-610 — the notice a payroll tile carries when at least one required rate is missing. `pairs`
 * is the server's "AED→SAR" list; it is shown verbatim so HR knows exactly which rate to add.
 */
function missingRateNotice(
  pairs: string[] | undefined,
  t: (key: string, fallback?: string) => string,
): KPIMetric['notice'] | undefined {
  if (!pairs || pairs.length === 0) return undefined;
  return {
    text: `${t('payroll.fx.missingRate', 'Exchange rate not configured')} (${pairs.join(', ')})`,
    href: FX_SETTINGS_PATH,
    linkLabel: t('payroll.fx.configureRates', 'Set exchange rates'),
  };
}

/**
 * Compute change between first and last entry in a trend series.
 * Returns null when fewer than 2 points or any value is missing.
 */
function getChangeFromTrend<T extends { [key: string]: unknown }>(
  trend: T[] | undefined,
  valueKey: keyof T
): { change: string; trend: 'up' | 'down' } | null {
  if (!trend || trend.length < 2) return null;
  const first = trend[0][valueKey] as number | undefined;
  const last = trend[trend.length - 1][valueKey] as number | undefined;
  if (first == null || last == null) return null;
  return formatChange(last, first);
}

// =============================================================================
// Attendance Mapping
// =============================================================================

export function mapAttendanceToKPIs(
  data: AttendanceOverview,
  t: (key: string, fallback?: string) => string
): { primary: KPIMetric; secondary: KPIMetric[] } {
  const presentChange = getChangeFromTrend(data.trend, 'presentRate');
  const lateChange = getChangeFromTrend(data.trend, 'lateRate');

  const primary: KPIMetric = {
    id: 'primary-attendance',
    title: t('attendance.present', 'Present %'),
    value: formatPercentage(data.presentRate),
    ...(presentChange ?? {}),
    icon: 'CheckCircle',
    color: 'green',
    category: 'attendance',
    description: t(
      'attendance.present_tooltip',
      'Share of expected workdays with recorded clock-in'
    ),
  };

  const secondary: KPIMetric[] = [
    {
      id: 'att-late',
      title: t('attendance.late', 'Late %'),
      value: formatPercentage(data.lateRate),
      // For late %, down is good — invert the trend indicator
      ...(lateChange
        ? { change: lateChange.change, trend: lateChange.trend === 'up' ? 'down' as const : 'up' as const }
        : {}),
      icon: 'Clock',
      color: 'orange',
      category: 'attendance',
      description: t(
        'attendance.late_tooltip',
        'Fraction of shifts clocked-in later than scheduled'
      ),
    },
    {
      id: 'att-hours',
      title: t('attendance.avg_hours', 'Avg Hours per Shift'),
      value: `${(data.avgHours ?? 0).toFixed(1)} ${t('hrs')}`,
      icon: 'Timer',
      color: 'blue',
      category: 'attendance',
      description: t(
        'attendance.avg_hours_tooltip',
        'Average worked hours per completed shift'
      ),
    },
    {
      id: 'att-overtime',
      title: t('attendance.overtime', 'Overtime'),
      value: `${(data.overtimeHours ?? 0).toFixed(0)} ${t('hrs')}`,
      icon: 'TrendingUp',
      color: 'purple',
      category: 'attendance',
      description: t(
        'attendance.overtime_tooltip',
        'Total overtime hours worked above scheduled time'
      ),
    },
  ];

  return { primary, secondary };
}

// =============================================================================
// Payroll Mapping
// =============================================================================

export function mapPayrollToKPIs(
  data: PayrollOverview,
  currency: string,
  t: (key: string, fallback?: string) => string
): { primary: KPIMetric; secondary: KPIMetric[] } {
  // CO-610 — the server names the currency every converted figure is in. The dashboard's own
  // label is only a fallback for a company with no currency configured; it must never RE-label a
  // total the server denominated in something else.
  const code = data.currencyCode || currency;
  const hasMissingRate = (data.missingRates?.length ?? 0) > 0;

  // Prefer the API's costDeltaPct; fall back to deriving from trend. With a missing rate the
  // server withholds the delta (partial totals compare nothing), and the trend is partial for the
  // same reason, so no comparison is derived from it either.
  const costChange =
    data.costDeltaPct != null
      ? {
          change: `${data.costDeltaPct >= 0 ? '+' : ''}${data.costDeltaPct.toFixed(1)}%`,
          trend: (data.costDeltaPct >= 0 ? 'up' : 'down') as 'up' | 'down',
        }
      : hasMissingRate
        ? null
        : getChangeFromTrend(data.trend, 'cost');

  const notice = missingRateNotice(data.missingRates, t);

  const primary: KPIMetric = {
    id: 'primary-payroll',
    title: t('payroll.cost', 'Payroll Cost'),
    value: formatCurrency(data.currentCost, code),
    ...(costChange ?? {}),
    icon: 'DollarSign',
    color: 'green',
    category: 'payroll',
    description: t(
      'payroll.cost_tooltip',
      'Total payroll expenditure for the selected period'
    ),
    supplement: formatUnconverted(data.unconverted),
    notice,
  };

  const pendingClaims = data.claims?.pending ?? 0;

  const secondary: KPIMetric[] = [
    {
      id: 'pay-claims',
      title: t('payroll.claims', 'Pending Claims'),
      value: pendingClaims.toString(),
      icon: 'FileText',
      color: 'orange',
      category: 'payroll',
      description: t(
        'payroll.claims_tooltip',
        'Number of expense claims awaiting approval'
      ),
    },
    {
      id: 'pay-benefits',
      title: t('payroll.benefits', 'Benefits Cost'),
      value: formatCurrency(data.benefitsCost, code),
      icon: 'Heart',
      color: 'blue',
      category: 'payroll',
      description: t(
        'payroll.benefits_tooltip',
        'Total cost of employee benefits for the period'
      ),
      supplement: formatUnconverted(data.benefitsUnconverted),
      // The pair is already named on the primary tile; repeat the notice only where this tile
      // itself holds an unconverted amount, so the hint appears next to the number it explains.
      notice: (data.benefitsUnconverted?.length ?? 0) > 0 ? notice : undefined,
    },
  ];

  return { primary, secondary };
}

// =============================================================================
// Employees Mapping
// =============================================================================

export function mapEmployeesToKPIs(
  data: EmployeesOverview,
  t: (key: string, fallback?: string) => string
): { primary: KPIMetric; secondary: KPIMetric[] } {
  const netChange = (data.hires ?? 0) - (data.exits ?? 0);
  const headcountChange = getChangeFromTrend(data.trend, 'headcount');

  const primary: KPIMetric = {
    id: 'primary-employees',
    title: t('employees.headcount', 'Headcount'),
    value: (data.headcount ?? 0).toString(),
    ...(headcountChange ?? {}),
    icon: 'Users',
    color: 'blue',
    category: 'employee',
    description: t(
      'employees.headcount_tooltip',
      'Total number of active employees'
    ),
  };

  const secondary: KPIMetric[] = [
    {
      id: 'emp-movement',
      title: t('employees.hires_exits', 'Hires / Exits'),
      value: `${data.hires ?? 0} / ${data.exits ?? 0}`,
      // Net delta is informational, not a vs-previous comparison
      change: t('employees.net', 'net') + ` ${netChange >= 0 ? '+' : ''}${netChange}`,
      isNeutralChange: true,
      icon: 'UserPlus',
      color: 'green',
      category: 'employee',
      description: t(
        'employees.hires_exits_tooltip',
        'New hires vs departures for the period'
      ),
    },
    {
      id: 'emp-turnover',
      title: t('employees.turnover', 'Turnover %'),
      value: formatPercentage(data.turnover),
      icon: 'RefreshCw',
      color: 'orange',
      category: 'employee',
      description: t(
        'employees.turnover_tooltip',
        'Employee turnover rate for the period'
      ),
    },
  ];

  return { primary, secondary };
}

// =============================================================================
// Projects Mapping
// =============================================================================

export function mapProjectsToKPIs(
  data: ProjectsOverview,
  t: (key: string, fallback?: string) => string
): { primary: KPIMetric; secondary: KPIMetric[] } {
  const primary: KPIMetric = {
    id: 'primary-projects',
    title: t('projects.active', 'Active Projects'),
    value: (data.active ?? 0).toString(),
    icon: 'Briefcase',
    color: 'purple',
    category: 'project',
    description: t(
      'projects.active_tooltip',
      'Number of currently active projects'
    ),
  };

  const secondary: KPIMetric[] = [
    {
      id: 'proj-ontrack',
      title: t('projects.on_track', 'On Track %'),
      value: formatPercentage(data.onTrackPct),
      icon: 'Target',
      color: 'green',
      category: 'project',
      description: t(
        'projects.on_track_tooltip',
        'Percentage of projects progressing on schedule'
      ),
    },
    {
      id: 'proj-overdue',
      title: t('projects.overdue', 'Overdue Tasks'),
      value: (data.overdue ?? 0).toString(),
      icon: 'AlertTriangle',
      color: 'red',
      category: 'project',
      description: t(
        'projects.overdue_tooltip',
        'Number of tasks past their due date'
      ),
    },
  ];

  return { primary, secondary };
}

// =============================================================================
// Holidays Mapping
// =============================================================================

/**
 * Map upcoming holidays API response to HolidayCard props.
 * Calculates daysUntil from the holiday date.
 */
export function mapHolidaysToCards(
  holidays: Array<{ name: string; date: string }>
): HolidayCardData[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return holidays.map((holiday, index) => {
    const holidayDate = new Date(holiday.date);
    holidayDate.setHours(0, 0, 0, 0);
    const diffTime = holidayDate.getTime() - today.getTime();
    const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      id: `holiday-${index}`,
      name: holiday.name,
      date: holiday.date,
      daysUntil: Math.max(0, daysUntil),
    };
  });
}
